package top.wcpe.allincore.dsl.compiler.lex

/**
 * MVP 阶段冻结的 token 类型集合（约 25 个）。
 *
 * 字符串插值采用 STRING_PART + INTERPOLATION_START/END 拆分形式：
 *
 * - 无插值字符串 → 单个 [STRING]
 * - 含插值字符串 → STRING_PART(可空)、INTERPOLATION_START、内部 token...、INTERPOLATION_END、STRING_PART...
 */
enum class TokenType {
    // ===== 字面量 / 标识符 =====
    IDENTIFIER,
    STRING,
    NUMBER,
    BOOLEAN,
    NULL,

    // ===== 标点 / 运算符 =====
    LPAREN,
    RPAREN,
    LBRACE,
    RBRACE,
    /** M6-a：Map 字面量起始 `#{`（井号紧贴左大括号；中间不允许空白）。 */
    HASH_LBRACE,
    LBRACKET,
    RBRACKET,
    COMMA,
    DOT,
    EQUALS,
    PLUS,
    MINUS,
    STAR,
    SLASH,
    PERCENT,
    SEMICOLON,
    COLON,
    DOUBLE_COLON,
    QUESTION,
    AT,

    // ===== 比较运算符 =====
    EQ_EQ,
    NOT_EQ,
    GT,
    LT,
    GT_EQ,
    LT_EQ,

    // ===== 逻辑运算符 =====
    AND_AND,
    OR_OR,
    BANG,
    /** M10 Stage 6：`!!` 非空断言后缀运算符。 */
    BANG_BANG,

    // ===== Kotlin 特殊运算符 =====
    SAFE_CALL,
    ELVIS,
    RANGE,
    /** `->` 用于 when 分支体起始（C2）。 */
    ARROW,

    // ===== 字符串插值 =====
    /** 含插值字符串的开始引号（值为空 lexeme `"` 用作分隔标记）。 */
    INTERPOLATED_STRING_START,

    /** 字符串中的纯文本片段（lexeme 为已解码后的文本）。 */
    STRING_PART,

    /** `${` —— 进入插值表达式。 */
    INTERPOLATION_START,

    /** `}` —— 结束插值表达式（与对应 INTERPOLATION_START 配对）。 */
    INTERPOLATION_END,

    // ===== 关键字 =====
    KW_SCREEN,
    /** Stage 11：`hud { ... }` 顶层块关键字，与 KW_SCREEN 平级；DSL 语法层完全等价。 */
    KW_HUD,
    /** Stage4：screen 级 `papi { ... }` 占位符块关键字。 */
    KW_PAPI,
    KW_STATE,
    /** session state 命名空间关键字：跨 screen / hud 共享的同名 Signal。 */
    KW_SESSION,
    /** persist state 命名空间关键字：玩家退出时落盘到本地 JSON 的持久化 Signal。 */
    KW_PERSIST,
    /** derived 命名空间关键字：声明式派生值（Computed），`derived defaults { ... }` 块与 `derived.x` 引用。 */
    KW_DERIVED,
    KW_DEFAULTS,
    KW_ROOT,
    KW_THEME,
    KW_ANIMATION,
    KW_TRANSITION,
    KW_KEYFRAME,
    KW_SEQUENCE,
    KW_PARALLEL,
    KW_STAGGER,
    KW_PATH_TRANSITION,
    KW_TRACKS,
    KW_TRACK,
    KW_FRAME,
    KW_COMPONENT,
    KW_PROPS,
    KW_CHILDREN,
    KW_STRING,
    KW_NUMBER,
    KW_BOOL,
    KW_COLOR,

    // ===== Canvas DSL 关键字（M11.6） =====
    /** `canvas` 元素块起始。 */
    KW_CANVAS,
    /** `draw { ... }` 子块。 */
    KW_DRAW,
    /** `once { ... }` 子块（首帧一次性指令）。 */
    KW_ONCE,

    // ===== Kotlin 通用关键字 =====
    KW_FUN,
    KW_VAR,
    /** `val`（只读局部声明），当前仅用于 canvas draw 块。 */
    KW_VAL,
    KW_IF,
    KW_ELSE,
    KW_WHEN,
    KW_WHILE,
    KW_FOR,
    KW_IN,
    KW_UNTIL,
    KW_BREAK,
    KW_CONTINUE,
    KW_RETURN,
    KW_TRY,
    KW_CATCH,
    KW_FINALLY,
    KW_AWAIT,
    /** M5-c：`is` 类型判断运算符。 */
    KW_IS,

    /** M6-a：`data` 顶层数据建模关键字。 */
    KW_DATA,

    /** Stage 7：`object` 顶层声明关键字（与 `data` 平级）。 */
    KW_OBJECT,

    // ===== 行边界 / 文件结束 =====
    NEWLINE,
    EOF,
}
